import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { useUser } from '../context/UserContext';
import { 
  Clock, 
  Target, 
  Trophy,
  CheckCircle,
  XCircle,
  Users,
  TrendingUp,
  Award,
  Timer
} from 'lucide-react';

const QuizInterface = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  
  const [session, setSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [timeLeft, setTimeLeft] = useState(15);
  const [totalTimeLeft, setTotalTimeLeft] = useState(600); // 10 minutes
  const [score, setScore] = useState(0);
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [lastAnswerResult, setLastAnswerResult] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [isSkipped, setIsSkipped] = useState(false);

  // Fetch session and start timers
  useEffect(() => {
    fetchQuizSession();
  }, [sessionId]);

  // Question timer
  useEffect(() => {
    if (!answered && !quizCompleted && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !answered) {
      // Auto skip when time runs out with -2 points penalty
      handleSkipQuestion(true);
    }
  }, [timeLeft, answered, quizCompleted]);

  // Total quiz timer
  useEffect(() => {
    if (!quizCompleted && totalTimeLeft > 0) {
      const timer = setTimeout(() => {
        setTotalTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (totalTimeLeft === 0) {
      // Quiz time expired
      finishQuiz();
    }
  }, [totalTimeLeft, quizCompleted]);

  const fetchQuizSession = async () => {
    setLoading(true);
    
    // Try to fetch real data first
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${backendUrl}/api/quiz/session/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.id) {
          setSession(data);
          setQuestionIndex(data.current_question || 0);
          setScore(data.score || 0);
          setConsecutiveCorrect(data.consecutive_correct || 0);
          
          if (data.questions && data.questions[data.current_question || 0]) {
            setCurrentQuestion(data.questions[data.current_question || 0]);
          }
          
          if (data.status === 'completed') {
            setQuizCompleted(true);
            fetchLeaderboard();
          }
          
          setLoading(false);
          return;
        }
      }
    } catch (error) {
      console.log('Backend not available, using demo quiz:', error);
    }

    // Fallback to demo data with correct format
    const demoQuestions = [
      // Easy Questions (1-10)
      {
        id: '1',
        question_text_english: 'Which city is the capital of Maharashtra?',
        question_text_hindi: 'महाराष्ट्र की राजधानी कौन सा शहर है?',
        options: [
          'A) Mumbai / मुंबई',
          'B) Pune / पुणे',
          'C) Nagpur / नागपुर',
          'D) Nashik / नाशिक'
        ],
        correct_answer: 0,
        difficulty: 'easy'
      },
      {
        id: '2',
        question_text_english: 'What is the largest desert in the world?',
        question_text_hindi: 'दुनिया का सबसे बड़ा रेगिस्तान कौन सा है?',
        options: [
          'A) Sahara / सहारा',
          'B) Gobi / गोबी',
          'C) Antarctic Desert / अंटार्कटिक रेगिस्तान',
          'D) Thar / थार'
        ],
        correct_answer: 2,
        difficulty: 'easy'
      },
      {
        id: '3',
        question_text_english: 'Who wrote "Pride and Prejudice"?',
        question_text_hindi: '"प्राइड एंड प्रेजुडिस" किसने लिखा?',
        options: [
          'A) Jane Austen / जेन ऑस्टेन',
          'B) Charles Dickens / चार्ल्स डिकेंस',
          'C) Emily Brontë / एमिली ब्रोंटे',
          'D) George Orwell / जॉर्ज ऑरवेल'
        ],
        correct_answer: 0,
        difficulty: 'easy'
      },
      {
        id: '4',
        question_text_english: 'Which element is the most abundant in the Earth\'s crust?',
        question_text_hindi: 'पृथ्वी की पपड़ी में सबसे अधिक प्रचुर तत्व कौन सा है?',
        options: [
          'A) Oxygen / ऑक्सीजन',
          'B) Silicon / सिलिकॉन',
          'C) Aluminum / एल्युमिनियम',
          'D) Iron / लोहा'
        ],
        correct_answer: 0,
        difficulty: 'easy'
      },
      {
        id: '5',
        question_text_english: 'What is the national bird of India?',
        question_text_hindi: 'भारत का राष्ट्रीय पक्षी क्या है?',
        options: [
          'A) Peacock / मोर',
          'B) Sparrow / गौरैया',
          'C) Parrot / तोता',
          'D) Eagle / चील'
        ],
        correct_answer: 0,
        difficulty: 'easy'
      },
      {
        id: '6',
        question_text_english: 'Who is the author of "The Jungle Book"?',
        question_text_hindi: '"द जंगल बुक" के लेखक कौन हैं?',
        options: [
          'A) Rudyard Kipling / रुडयार्ड किपलिंग',
          'B) J.K. Rowling / जे.के. राउलिंग',
          'C) C.S. Lewis / सी.एस. लुईस',
          'D) Roald Dahl / रोल्ड डाहल'
        ],
        correct_answer: 0,
        difficulty: 'easy'
      },
      {
        id: '7',
        question_text_english: 'Which Indian state is known as the "Land of Five Rivers"?',
        question_text_hindi: 'किस भारतीय राज्य को "पांच नदियों की भूमि" के रूप में जाना जाता है?',
        options: [
          'A) Punjab / पंजाब',
          'B) Haryana / हरियाणा',
          'C) Uttar Pradesh / उत्तर प्रदेश',
          'D) Bihar / बिहार'
        ],
        correct_answer: 0,
        difficulty: 'easy'
      },
      {
        id: '8',
        question_text_english: 'What is the chemical symbol for water?',
        question_text_hindi: 'पानी का रासायनिक प्रतीक क्या है?',
        options: [
          'A) H2O / एच2ओ',
          'B) CO2 / सीओ2',
          'C) O2 / ओ2',
          'D) NaCl / नैक्ल'
        ],
        correct_answer: 0,
        difficulty: 'easy'
      },
      {
        id: '9',
        question_text_english: 'Who was the first Indian to win a Nobel Prize?',
        question_text_hindi: 'नोबेल पुरस्कार जीतने वाले पहले भारतीय कौन थे?',
        options: [
          'A) Rabindranath Tagore / रवींद्रनाथ टैगोर',
          'B) C.V. Raman / सी.वी. रमन',
          'C) Amartya Sen / अमर्त्य सेन',
          'D) Mother Teresa / मदर टेरेसा'
        ],
        correct_answer: 0,
        difficulty: 'easy'
      },
      {
        id: '10',
        question_text_english: 'Which festival is celebrated as the victory of light over darkness?',
        question_text_hindi: 'कौन सा त्योहार अंधकार पर प्रकाश की जीत के रूप में मनाया जाता है?',
        options: [
          'A) Diwali / दिवाली',
          'B) Holi / होली',
          'C) Eid / ईद',
          'D) Christmas / क्रिसमस'
        ],
        correct_answer: 0,
        difficulty: 'easy'
      },
      // Medium Questions (11-15)
      {
        id: '11',
        question_text_english: 'What is the main language spoken in Andhra Pradesh?',
        question_text_hindi: 'आंध्र प्रदेश में बोली जाने वाली मुख्य भाषा क्या है?',
        options: [
          'A) Telugu / तेलुगु',
          'B) Tamil / तमिल',
          'C) Kannada / कन्नड़',
          'D) Malayalam / मलयालम'
        ],
        correct_answer: 0,
        difficulty: 'medium'
      },
      {
        id: '12',
        question_text_english: 'Who is the author of "The Interpretation of Dreams"?',
        question_text_hindi: '"द इंटरप्रिटेशन ऑफ ड्रीम्स" के लेखक कौन हैं?',
        options: [
          'A) Sigmund Freud / सिगमंड फ्रायड',
          'B) Carl Jung / कार्ल जंग',
          'C) B.F. Skinner / बी.एफ. स्किनर',
          'D) Jean Piaget / जीन पियाजे'
        ],
        correct_answer: 0,
        difficulty: 'medium'
      },
      {
        id: '13',
        question_text_english: 'Which Indian leader was known as the "Iron Man of India"?',
        question_text_hindi: 'किस भारतीय नेता को "भारत के लौह पुरुष" के रूप में जाना जाता है?',
        options: [
          'A) Sardar Patel / सरदार पटेल',
          'B) Jawaharlal Nehru / जवाहरलाल नेहरू',
          'C) Subhas Chandra Bose / सुभाष चंद्र बोस',
          'D) Bhagat Singh / भगत सिंह'
        ],
        correct_answer: 0,
        difficulty: 'medium'
      },
      {
        id: '14',
        question_text_english: 'What is the largest planet in our solar system?',
        question_text_hindi: 'हमारे सौर मंडल का सबसे बड़ा ग्रह कौन सा है?',
        options: [
          'A) Earth / पृथ्वी',
          'B) Saturn / शनि',
          'C) Jupiter / बृहस्पति',
          'D) Uranus / यूरेनस'
        ],
        correct_answer: 2,
        difficulty: 'medium'
      },
      {
        id: '15',
        question_text_english: 'Who composed the Indian national anthem?',
        question_text_hindi: 'भारतीय राष्ट्रगान की रचना किसने की?',
        options: [
          'A) Rabindranath Tagore / रवींद्रनाथ टैगोर',
          'B) A.R. Rahman / ए.आर. रहमान',
          'C) C. Ramchandra / सी. रामचंद्र',
          'D) Hemant Kumar / हेमंत कुमार'
        ],
        correct_answer: 0,
        difficulty: 'medium'
      },
      // Hard Questions (16-20)
      {
        id: '16',
        question_text_english: 'What is the name of the process by which water moves through a plant?',
        question_text_hindi: 'पौधे के माध्यम से पानी की गति की प्रक्रिया का नाम क्या है?',
        options: [
          'A) Respiration / श्वसन',
          'B) Transpiration / वाष्पोत्सर्जन',
          'C) Photosynthesis / प्रकाश संश्लेषण',
          'D) Osmosis / परासरण'
        ],
        correct_answer: 1,
        difficulty: 'hard'
      },
      {
        id: '17',
        question_text_english: 'Who is credited with developing the theory of relativity?',
        question_text_hindi: 'सापेक्षता के सिद्धांत को विकसित करने का श्रेय किसे दिया जाता है?',
        options: [
          'A) Albert Einstein / अल्बर्ट आइंस्टीन',
          'B) Isaac Newton / आइज़क न्यूटन',
          'C) Galileo Galilei / गैलीलियो गैलिली',
          'D) Stephen Hawking / स्टीफन हॉकिंग'
        ],
        correct_answer: 0,
        difficulty: 'hard'
      },
      {
        id: '18',
        question_text_english: 'What is the term for the study of the structure, behavior, and evolution of the universe?',
        question_text_hindi: 'ब्रह्मांड की संरचना, व्यवहार और विकास के अध्ययन के लिए शब्द क्या है?',
        options: [
          'A) Cosmology / ब्रह्मांड विज्ञान',
          'B) Astronomy / खगोल विज्ञान',
          'C) Astrology / ज्योतिष',
          'D) Physics / भौतिकी'
        ],
        correct_answer: 0,
        difficulty: 'hard'
      },
      {
        id: '19',
        question_text_english: 'Who discovered the neutron?',
        question_text_hindi: 'न्यूट्रॉन की खोज किसने की?',
        options: [
          'A) Ernest Rutherford / अर्नेस्ट रदरफोर्ड',
          'B) James Chadwick / जेम्स चैडविक',
          'C) Niels Bohr / नील्स बोहर',
          'D) Enrico Fermi / एनरिको फर्मी'
        ],
        correct_answer: 1,
        difficulty: 'hard'
      },
      {
        id: '20',
        question_text_english: 'What is the name of the Indian satellite series used for communication?',
        question_text_hindi: 'संचार के लिए उपयोग की जाने वाली भारतीय उपग्रह श्रृंखला का नाम क्या है?',
        options: [
          'A) INSAT / इनसैट',
          'B) IRS / आईआरएस',
          'C) EDUSAT / एडूसैट',
          'D) CARTOSAT / कार्टोसैट'
        ],
        correct_answer: 0,
        difficulty: 'hard'
      }
    ];

    const demoSession = {
      id: sessionId,
      contest_id: sessionId.includes('live_1') ? 'live_1' : sessionId.includes('live_2') ? 'live_2' : 'live_3',
      user_id: user?.id || 'demo_user',
      status: 'active',
      current_question: 0,
      score: 0,
      consecutive_correct: 0,
      questions: demoQuestions,
      total_questions: demoQuestions.length,
      time_remaining: 600,
      started_at: new Date().toISOString()
    };

    // Set demo data
    setSession(demoSession);
    setQuestionIndex(0);
    setScore(0);
    setConsecutiveCorrect(0);
    setCurrentQuestion(demoQuestions[0]);
    setLoading(false);
  };

  const fetchLeaderboard = async () => {
    // Demo leaderboard data
    const demoLeaderboard = [
      { rank: 1, username: "QuizMaster", score: 285, prize: "🏞️ Land Worth ₹12,00,000" },
      { rank: 2, username: "BrainChamp", score: 272, prize: "🏡 Residential Plot" },
      { rank: 3, username: "KnowledgeKing", score: 268, prize: "💻 MacBook Pro M3" },
      { rank: 4, username: "SmartPlayer", score: 251, prize: "📱 iPhone 15" },
      { rank: 5, username: user?.username || "You", score: score, prize: "📱 iPhone 15" },
      { rank: 6, username: "QuickThinker", score: 245, prize: "📱 iPhone 15" },
      { rank: 7, username: "GeniusUser", score: 238, prize: "📱 iPhone 15" },
      { rank: 8, username: "FastLearner", score: 232, prize: "📱 iPhone 15" },
      { rank: 9, username: "WiseStudent", score: 225, prize: "📱 iPhone 15" },
      { rank: 10, username: "CleverMind", score: 218, prize: "📱 iPhone 15" }
    ];

    // Set demo leaderboard
    setLeaderboard(demoLeaderboard);

    // Try to fetch real leaderboard in background
    try {
      const response = await fetch(`/api/quiz/leaderboard/${session?.contest_id}`);
      
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          setLeaderboard(data);
        }
      }
    } catch (error) {
      console.log('Backend not available, using demo leaderboard:', error);
      // Demo data already set
    }
  };

  const handleSkipQuestion = async (isAutoSkip = false) => {
    if (answered) return;
    
    setAnswered(true);
    setIsSkipped(true);
    setSelectedAnswer(-1);
    
    // Apply -2 points penalty for skipping
    const penaltyPoints = -2;
    const newTotalScore = Math.max(0, score + penaltyPoints);
    setScore(newTotalScore);
    setConsecutiveCorrect(0); // Reset streak when skipping
    
    setLastAnswerResult({
      is_correct: false,
      points_earned: penaltyPoints,
      total_score: newTotalScore,
      consecutive_correct: 0,
      bonus_breakdown: [isAutoSkip ? "Auto Skip Penalty: -2" : "Manual Skip Penalty: -2"],
      time_taken: 15,
      is_skipped: true,
      skip_type: isAutoSkip ? "auto" : "manual"
    });
    
    // Check if quiz should be completed
    if (session && questionIndex + 1 >= session.questions.length) {
      setQuizCompleted(true);
      fetchLeaderboard();
    } else {
      // Move to next question after 2 seconds
      setTimeout(() => {
        nextQuestion();
      }, 2000);
    }
  };

  const handleAnswerSubmit = async (answerIndex, timeTaken) => {
    if (answered) return;
    
    setAnswered(true);
    setSelectedAnswer(answerIndex);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('token');
      
      const answerData = {
        session_id: sessionId,
        question_index: questionIndex,
        selected_answer: answerIndex,
        time_taken: timeTaken,
        is_correct: answerIndex === currentQuestion.correct_answer,
        points_earned: 0
      };
      
      const response = await fetch(`${backendUrl}/api/quiz/answer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(answerData)
      });

      if (response.ok) {
        const result = await response.json();
        setScore(result.total_score);
        setConsecutiveCorrect(result.consecutive_correct);
        setLastAnswerResult(result);
        
        if (result.quiz_completed) {
          setQuizCompleted(true);
          fetchLeaderboard();
        } else {
          // Move to next question after 2 seconds
          setTimeout(() => {
            nextQuestion();
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      // Fallback for demo mode - proceed to next question
      console.log('Backend not available, using demo mode progression');
      
      // Calculate demo score with comprehensive logic
      const isCorrect = answerIndex === currentQuestion.correct_answer;
      let points = 0;
      let bonusBreakdown = [];
      
      if (isCorrect) {
        // Base points for correct answer
        points = 20;
        bonusBreakdown.push("Correct Answer: +20");
        
        // Time bonus (remaining seconds)
        const timeBonus = Math.max(0, 15 - timeTaken);
        points += timeBonus;
        if (timeBonus > 0) {
          bonusBreakdown.push(`Time Bonus: +${timeBonus}`);
        }
        
        const newConsecutive = consecutiveCorrect + 1;
        setConsecutiveCorrect(newConsecutive);
        
        // Streak bonuses
        if (newConsecutive === 3) {
          points += 10;
          bonusBreakdown.push("3-Streak Bonus: +10");
        } else if (newConsecutive === 5) {
          points += 20;
          bonusBreakdown.push("5-Streak Bonus: +20");
        } else if (newConsecutive === 10) {
          points += 50;
          bonusBreakdown.push("10-Streak Bonus: +50");
        }
      } else {
        // Wrong answer penalty
        points = -4;
        bonusBreakdown.push("Wrong Answer: -4");
        setConsecutiveCorrect(0);
      }
      
      const newTotalScore = Math.max(0, score + points);
      setScore(newTotalScore);
      setLastAnswerResult({
        is_correct: isCorrect,
        points_earned: points,
        total_score: newTotalScore,
        consecutive_correct: isCorrect ? consecutiveCorrect + 1 : 0,
        bonus_breakdown: bonusBreakdown,
        time_taken: timeTaken
      });
      
      // Check if quiz should be completed
      if (session && questionIndex + 1 >= session.questions.length) {
        setQuizCompleted(true);
        fetchLeaderboard();
      } else {
        // Move to next question after 2 seconds
        setTimeout(() => {
          nextQuestion();
        }, 2000);
      }
    }
  };

  const nextQuestion = () => {
    if (session && questionIndex + 1 < session.questions.length) {
      const nextIndex = questionIndex + 1;
      setQuestionIndex(nextIndex);
      setCurrentQuestion(session.questions[nextIndex]);
      setSelectedAnswer(null);
      setAnswered(false);
      setIsSkipped(false);
      setTimeLeft(15);
      setLastAnswerResult(null);
    } else {
      finishQuiz();
    }
  };

  const finishQuiz = () => {
    setQuizCompleted(true);
    fetchLeaderboard();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getUserRank = () => {
    const userEntry = leaderboard.find(entry => entry.user_id === user.id);
    return userEntry ? userEntry.rank : '-';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading quiz...</p>
        </div>
      </div>
    );
  }

  if (quizCompleted) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Results Header */}
          <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white mb-6">
            <CardContent className="p-8 text-center">
              <Trophy className="h-12 w-12 mx-auto mb-4" />
              <h1 className="text-3xl font-bold mb-2">Quiz Completed!</h1>
              <p className="text-green-100">Great job! Here are your results.</p>
            </CardContent>
          </Card>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Your Performance */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-xl">Your Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <TrendingUp className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900">{score}</div>
                    <div className="text-sm text-gray-600">Total Score</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <Award className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900">#{getUserRank()}</div>
                    <div className="text-sm text-gray-600">Your Rank</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <CheckCircle className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900">{session?.correct_answers || 0}</div>
                    <div className="text-sm text-gray-600">Correct</div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <XCircle className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900">{session?.wrong_answers || 0}</div>
                    <div className="text-sm text-gray-600">Wrong</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <Button 
                    onClick={() => setShowLeaderboard(!showLeaderboard)}
                    className="w-full"
                    variant="outline"
                  >
                    {showLeaderboard ? 'Hide' : 'Show'} Full Leaderboard
                  </Button>
                  
                  <div className="flex space-x-4">
                    <Button 
                      onClick={() => navigate('/dashboard')}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                    >
                      Back to Dashboard
                    </Button>
                    <Button 
                      onClick={() => navigate('/my-contests')}
                      variant="outline"
                      className="flex-1"
                    >
                      My Contests
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contest Stats</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Participants</span>
                    <span className="font-semibold">{leaderboard.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Average Score</span>
                    <span className="font-semibold">
                      {leaderboard.length > 0 ? 
                        Math.round(leaderboard.reduce((acc, p) => acc + p.score, 0) / leaderboard.length) : 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Highest Score</span>
                    <span className="font-semibold text-green-600">
                      {leaderboard.length > 0 ? leaderboard[0].score : 0}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Leaderboard */}
          {showLeaderboard && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-xl flex items-center">
                  <Trophy className="h-5 w-5 mr-2 text-yellow-500" />
                  Contest Leaderboard
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {leaderboard.slice(0, 20).map((participant, index) => (
                    <div 
                      key={participant.user_id}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        participant.user_id === user.id ? 'bg-red-50 border-2 border-red-200' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                          index === 0 ? 'bg-yellow-500' : 
                          index === 1 ? 'bg-gray-400' : 
                          index === 2 ? 'bg-orange-500' : 'bg-gray-300'
                        }`}>
                          {participant.rank}
                        </div>
                        <div>
                          <div className="font-semibold">
                            {participant.username}
                            {participant.user_id === user.id && (
                              <Badge className="ml-2 bg-red-600">You</Badge>
                            )}
                          </div>
                          <div className="text-sm text-gray-600">
                            {participant.correct_answers}/{participant.correct_answers + participant.wrong_answers} correct
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-lg">{participant.score}</div>
                        <div className="text-sm text-gray-600">points</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-4xl mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Badge className="bg-red-600 text-white">🔥 LIVE QUIZ</Badge>
            <div>
              <div className="font-semibold">Question {questionIndex + 1} of {session?.questions.length || 0}</div>
              <div className="text-sm text-gray-600">Score: {score} points</div>
            </div>
          </div>
          
          <div className="flex items-center space-x-6">
            <div className="text-center">
              <div className="text-lg font-bold text-red-600">{formatTime(timeLeft)}</div>
              <div className="text-xs text-gray-600">Question Timer</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">{formatTime(totalTimeLeft)}</div>
              <div className="text-xs text-gray-600">Total Time Left</div>
            </div>
            {consecutiveCorrect > 0 && (
              <div className="text-center">
                <div className="text-lg font-bold text-green-600">🔥 {consecutiveCorrect}</div>
                <div className="text-xs text-gray-600">Streak</div>
              </div>
            )}
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="max-w-4xl mx-auto px-4 mt-3">
          <Progress 
            value={(questionIndex / (session?.questions.length || 1)) * 100} 
            className="h-2"
          />
        </div>
      </div>

      {/* Question Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {currentQuestion && (
          <Card className="shadow-lg">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <CardTitle className="text-xl mb-2">
                    {currentQuestion.question_text_english}
                  </CardTitle>
                  <p className="text-gray-600 mb-4">
                    {currentQuestion.question_text_hindi}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-red-600">{timeLeft}</div>
                  <div className="text-sm text-gray-600">seconds</div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {currentQuestion.options.map((option, index) => (
                  <Button
                    key={index}
                    onClick={() => !answered && handleAnswerSubmit(index, 15 - timeLeft)}
                    disabled={answered}
                    className={`h-auto p-4 text-left justify-start whitespace-normal ${
                      answered 
                        ? (index === currentQuestion.correct_answer 
                            ? 'bg-green-500 hover:bg-green-500 text-white' 
                            : selectedAnswer === index 
                              ? isSkipped ? 'bg-yellow-500 hover:bg-yellow-500 text-white' : 'bg-red-500 hover:bg-red-500 text-white'
                              : 'bg-gray-100 text-gray-600')
                        : selectedAnswer === index
                          ? 'bg-red-100 border-red-300'
                          : 'bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                    variant="outline"
                  >
                    <div>
                      <div className="font-semibold mb-1">{option}</div>
                      {answered && index === currentQuestion.correct_answer && (
                        <CheckCircle className="h-4 w-4 inline ml-2" />
                      )}
                      {answered && selectedAnswer === index && index !== currentQuestion.correct_answer && !isSkipped && (
                        <XCircle className="h-4 w-4 inline ml-2" />
                      )}
                    </div>
                  </Button>
                ))}
              </div>

              {/* Skip Question Button */}
              {!answered && (
                <div className="flex justify-center mb-4">
                  <Button
                    onClick={() => handleSkipQuestion(false)}
                    variant="outline"
                    className="bg-yellow-50 text-yellow-700 border-yellow-300 hover:bg-yellow-100 px-6 py-2"
                  >
                    ⏭️ Skip Question (-2 points)
                  </Button>
                </div>
              )}

              {/* Answer Result */}
              {lastAnswerResult && (
                <div className={`mt-6 p-4 rounded-lg border ${
                  lastAnswerResult.is_skipped 
                    ? 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200'
                    : lastAnswerResult.is_correct 
                      ? 'bg-gradient-to-r from-green-50 to-green-100 border-green-200'
                      : 'bg-gradient-to-r from-red-50 to-red-100 border-red-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className={`text-lg font-semibold ${
                        lastAnswerResult.is_skipped 
                          ? 'text-yellow-600'
                          : lastAnswerResult.is_correct 
                            ? 'text-green-600' 
                            : 'text-red-600'
                      }`}>
                        {lastAnswerResult.is_skipped 
                          ? `⏭️ Question ${lastAnswerResult.skip_type === 'auto' ? 'Auto-' : ''}Skipped`
                          : lastAnswerResult.is_correct 
                            ? '✅ Correct!' 
                            : '❌ Wrong Answer'}
                      </div>
                      <div className="text-sm text-gray-600">
                        Points earned: {lastAnswerResult.points_earned > 0 ? '+' : ''}{lastAnswerResult.points_earned}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-lg font-bold ${
                        lastAnswerResult.is_skipped 
                          ? 'text-yellow-600'
                          : 'text-blue-600'
                      }`}>Total: {lastAnswerResult.total_score}</div>
                      {lastAnswerResult.consecutive_correct > 0 && (
                        <div className="text-sm text-green-600">🔥 {lastAnswerResult.consecutive_correct} streak!</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default QuizInterface;